/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {Switch} from '@app/components/form/Switch';
import styles from '@app/components/modals/ServiceWorkerLogsModal.module.css';
import * as Modal from '@app/components/modals/Modal';
import {Button} from '@app/components/uikit/button/Button';
import {
	appendSwLog,
	clearSwLogs,
	getAllSwLogs,
	isSwLoggingEnabled,
	setSwLoggingEnabled,
	SW_LOG_SET_ENABLED_MESSAGE,
	type SwLogRecord,
} from '@app/lib/ServiceWorkerLogDB';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

const notifyServiceWorker = async (enabled: boolean): Promise<void> => {
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
	try {
		const registration = await navigator.serviceWorker.getRegistration();
		const target = registration?.active ?? registration?.waiting ?? registration?.installing;
		target?.postMessage({type: SW_LOG_SET_ENABLED_MESSAGE, enabled});
	} catch {
		/* ignore */
	}
};

const formatTimestamp = (ts: number): string => {
	try {
		const d = new Date(ts);
		return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {hour12: false})}.${String(d.getMilliseconds()).padStart(3, '0')}`;
	} catch {
		return String(ts);
	}
};

const stringifyData = (data: unknown): string => {
	if (data === undefined) return '';
	try {
		return JSON.stringify(data, null, 2);
	} catch {
		return String(data);
	}
};

const serializeForExport = (logs: ReadonlyArray<SwLogRecord>): string =>
	logs
		.slice()
		.reverse()
		.map((log) => {
			const base = `[${new Date(log.ts).toISOString()}] ${log.level.toUpperCase()} ${log.source}: ${log.message}`;
			return log.data !== undefined ? `${base}\n${stringifyData(log.data)}` : base;
		})
		.join('\n');

export const ServiceWorkerLogsModal: React.FC = () => {
	const {t} = useLingui();
	const [logs, setLogs] = useState<ReadonlyArray<SwLogRecord>>([]);
	const [enabled, setEnabled] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [filter, setFilter] = useState<string>('');
	const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

	const refresh = useCallback(async () => {
		const rows = await getAllSwLogs();
		setLogs(rows);
	}, []);

	useEffect(() => {
		let alive = true;
		(async () => {
			const [isEnabled] = await Promise.all([isSwLoggingEnabled(), refresh()]);
			if (!alive) return;
			setEnabled(isEnabled);
			setLoading(false);
		})();
		return () => {
			alive = false;
		};
	}, [refresh]);

	useEffect(() => {
		const id = window.setInterval(() => {
			void refresh();
		}, 2000);
		return () => window.clearInterval(id);
	}, [refresh]);

	const handleToggle = useCallback(
		async (next: boolean) => {
			setEnabled(next);
			await setSwLoggingEnabled(next);
			await notifyServiceWorker(next);
			await appendSwLog('info', 'ui', next ? 'logging enabled from settings' : 'logging disabled from settings');
			await refresh();
		},
		[refresh],
	);

	const handleClear = useCallback(async () => {
		await clearSwLogs();
		await refresh();
	}, [refresh]);

	const handleCopy = useCallback(async () => {
		const text = serializeForExport(logs);
		try {
			await navigator.clipboard.writeText(text);
			setCopyState('copied');
			window.setTimeout(() => setCopyState('idle'), 1500);
		} catch {
			/* ignore */
		}
	}, [logs]);

	const handleDownload = useCallback(() => {
		const blob = new Blob([JSON.stringify(logs.slice().reverse(), null, 2)], {type: 'application/json'});
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `fluxer-sw-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}, [logs]);

	const filteredLogs = useMemo(() => {
		const needle = filter.trim().toLowerCase();
		if (!needle) return logs;
		return logs.filter((log) => {
			if (log.message.toLowerCase().includes(needle)) return true;
			if (log.level.toLowerCase().includes(needle)) return true;
			if (log.source.toLowerCase().includes(needle)) return true;
			if (log.data !== undefined && stringifyData(log.data).toLowerCase().includes(needle)) return true;
			return false;
		});
	}, [filter, logs]);

	return (
		<Modal.Root size="large" className={styles.root}>
			<Modal.Header title={t`Service Worker Logs`} />
			<Modal.Content padding="none" contentClassName={styles.content}>
				<div className={styles.controls}>
					<Switch
						label={t`Enable background logging`}
						description={t`Persist service worker activity to this device so you can inspect it after the PWA is reopened.`}
						value={enabled}
						onChange={handleToggle}
					/>
					<input
						className={styles.filterInput}
						type="search"
						placeholder={t`Filter…`}
						value={filter}
						onChange={(event) => setFilter(event.currentTarget.value)}
					/>
					<div className={styles.meta}>
						{loading ? (
							<Trans>Loading…</Trans>
						) : (
							<Trans>
								{filteredLogs.length} of {logs.length} entries
							</Trans>
						)}
					</div>
				</div>
				<div className={styles.logList}>
					{filteredLogs.length === 0 ? (
						<div className={styles.empty}>
							{enabled ? (
								<Trans>No logs yet. Background events will appear here.</Trans>
							) : (
								<Trans>Logging is off. Enable it above to start recording service worker activity.</Trans>
							)}
						</div>
					) : (
						filteredLogs.map((log) => {
							const key = log.id ?? `${log.ts}-${log.message}`;
							return (
								<div key={String(key)} className={clsx(styles.logEntry, styles[`level_${log.level}`])}>
									<div className={styles.logEntryHeader}>
										<span className={styles.logTimestamp}>{formatTimestamp(log.ts)}</span>
										<span className={styles.logLevel}>{log.level.toUpperCase()}</span>
										<span className={styles.logSource}>{log.source}</span>
									</div>
									<div className={styles.logMessage}>{log.message}</div>
									{log.data !== undefined && <pre className={styles.logData}>{stringifyData(log.data)}</pre>}
								</div>
							);
						})
					)}
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={handleClear}>
					<Trans>Clear</Trans>
				</Button>
				<Button variant="secondary" onClick={handleDownload}>
					<Trans>Download JSON</Trans>
				</Button>
				<Button variant="secondary" onClick={handleCopy}>
					{copyState === 'copied' ? <Trans>Copied!</Trans> : <Trans>Copy</Trans>}
				</Button>
				<Button variant="primary" onClick={ModalActionCreators.pop}>
					<Trans>Close</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
};

export const openServiceWorkerLogsModal = (): void => {
	ModalActionCreators.push(ModalActionCreators.modal(() => <ServiceWorkerLogsModal />));
};
